import React from "react";
import Seo from "../components/Seo";
import Loading from "../components/Loading";
import "../css/Documentation.css";
import {BrokerBenchmark, parseBrokerResult} from "../components/BrokerBenchmark";
import tls50Url from "../xmq_test_results/Encryption-50K.txt";
import tls75Url from "../xmq_test_results/Encryption-75K.txt";
import tls100Url from "../xmq_test_results/Encryption-100K.txt";

const RATES = [
    {key: "50K", url: tls50Url, rate: 50000},
    {key: "75K", url: tls75Url, rate: 75000},
    {key: "100K", url: tls100Url, rate: 100000},
];

// A broker not run at a rate carries only a Status line: it is listed under the chart, not in it.
const notRun = (s) => (s.meta["Status"] || "").startsWith("NOT RUN");

export default class XMQ_tests_encryption extends React.Component
{
    state = {results: {}, error: null};

    componentDidMount()
    {
        RATES.forEach(({key, url}) =>
            fetch(url)
                .then((r) => r.text())
                .then((text) => this.setState((st) => ({results: {...st.results, [key]: parseBrokerResult(text)}})))
                .catch((err) => this.setState({error: String(err)})));
    }

    render()
    {
        const {results, error} = this.state;

        return <div key="tests-encryption" className="XMQ" style={{textAlign: "left", padding: 8}}>
            <Seo title="MQTT Performance Tests — Encryption (TLS)"
                 description="MQTT performance tests over TLS 1.3: point-to-point delivery at 50,000, 75,000 and 100,000 messages a second, XMQ against other MQTT servers."
                 keywords="MQTT performance tests, MQTT TLS, encrypted MQTT, fast MQTT server, XMQ"
                 path="/xmq_tests_encryption"/>
            <h1>MQTT Performance Tests: Encryption</h1>
            <h3>Point-to-Point over TLS 1.3</h3>

            <p>
                The point-to-point test with every client connected over TLS 1.3: 25,000 publishers,
                25,000 subscribers, each pair on its own topic, QoS&nbsp;1, a 16-byte payload, ten
                minutes per rate. Every broker runs at 50K messages a second; 75K and 100K only
                those whose median latency stayed under 100&nbsp;ms at the rate before.
            </p>

            <p>
                Mosquitto and EMQX are in the 50K table and in neither of the other two. Both ran at
                50K and both came in over the limit that gates the higher rates - EMQX at a
                2.9-second median, Mosquitto at 152.9 seconds, twenty-nine times and fifteen hundred
                times the 100&nbsp;ms allowed. Carrying either forward would have measured how long a
                queue takes to drain rather than how fast a broker delivers, so the 75K and 100K
                tables compare the three brokers that stayed in the microsecond range: XMQ, FlashMQ
                and HiveMQ.
            </p>

            <p>
                Encryption costs one TLS record and one system call per message, on both sides.
                The memory column shows the other cost: the TLS state of 50,000 connections.
            </p>

            {error && <p>Failed to load test results: {error}</p>}

            {RATES.map(({key, rate}) => {
                const servers = results[key];
                const measured = servers ? servers.filter((s) => !notRun(s)) : null;
                // The filter makes a new array, so the conditions the parser put on the full one
                // have to be carried across for the page to show them.
                if (measured)
                    measured.conditions = servers.conditions;
                const skipped = servers ? servers.filter(notRun) : [];
                return <div key={key}>
                    <h3>{key} messages a second</h3>
                    {!servers && !error && <Loading text="Loading results…"/>}
                    <BrokerBenchmark servers={measured} targetRate={rate}/>
                    {skipped.length > 0 && <p style={{fontSize: "0.9em", color: "#666"}}>
                        Not run at this rate:{" "}
                        {skipped.map((s) => `${s.name} (${s.meta["Status"].replace(/^NOT RUN - /, "")})`).join("; ")}.
                    </p>}
                </div>;
            })}

            <p style={{fontSize: "0.9em", color: "#666"}}>
                EMQX did not answer CONNECT for 17 of its 25,000 publishers and 10 of its 25,000
                subscribers within 60 seconds at 50K; those clients were left out of its run.
            </p>
        </div>;
    }
}
